import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { LineChart, Award, TrendingUp, TrendingDown, Archive, Loader2, User, Target, Shield, Activity, Lock } from "lucide-react";

export default function Profile() {
  const { t, fmtDate, fmtCurrency } = useApp();
  const [profile, setProfile] = useState(null);
  const [events, setEvents] = useState([]);
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [auditVerify, setAuditVerify] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [p, e, h, m, av] = await Promise.all([
        axios.get(`${API}/profile/analytics`),
        axios.get(`${API}/audit/events`, { params: { limit: 40 } }),
        axios.get(`${API}/health`),
        axios.get(`${API}/metrics`),
        axios.get(`${API}/audit/verify`),
      ]);
      setProfile(p.data);
      setEvents(e.data.events || []);
      setHealth(h.data);
      setMetrics(m.data);
      setAuditVerify(av.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <User size={22} className="text-accent-blue" />
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight" data-testid="profile-title">Profil & Journal</h1>
          <p className="text-white/50 text-sm">Analyse comportementale et traçabilité complète de vos décisions.</p>
        </div>
      </div>

      {/* Institutional observability strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="observability-strip">
        <Kpi label="Statut serveur" value={health?.status === "healthy" ? "🟢 Healthy" : "🔴 Down"}
             color={health?.status === "healthy" ? "text-up" : "text-down"} icon={Activity} />
        <Kpi label="Uptime" value={health ? `${Math.round(health.uptime_seconds / 60)} min` : "—"} icon={Activity} />
        <Kpi label="Journal cryptographique"
             value={auditVerify?.valid ? "🔒 Vérifié" : "⚠️ Rompu"}
             color={auditVerify?.valid ? "text-up" : "text-down"} icon={Lock} />
        <Kpi label="Événements audités" value={auditVerify?.events_checked || 0} icon={Shield} />
      </div>

      {loading && <div className="text-white/40 text-center py-10"><Loader2 className="animate-spin inline mr-2" /> Chargement…</div>}

      {!loading && profile && !profile.ok && (
        <div className="panel p-6 text-center" data-testid="profile-not-enough">
          <Target size={32} className="text-warn mx-auto mb-3" />
          <div className="text-white/70">{profile.message}</div>
          <div className="text-xs text-white/40 mt-2">Utilisez la page « Historique » pour marquer vos trades comme gagnés/perdus.</div>
        </div>
      )}

      {!loading && profile && profile.ok && (
        <>
      {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Trades clôturés" value={profile.n_trades} icon={Archive} />
            <Kpi label="Winrate global" value={`${profile.winrate}%`}
                 color={profile.winrate >= 50 ? "text-up" : "text-down"} icon={Award} />
            <Kpi label="Winrate ACHAT" value={`${profile.buy_winrate}%`} icon={TrendingUp}
                 color="text-up" />
            <Kpi label="Winrate VENTE" value={`${profile.sell_winrate}%`} icon={TrendingDown}
                 color="text-down" />
          </div>

          {/* Profile insights */}
          <div className="panel p-5 space-y-3" data-testid="profile-insights">
            <div className="panel-title text-[14px]">Insights personnalisés</div>
            <div className="bg-hover rounded-md p-3 border border-accent-blue/20">
              <div className="text-[11px] uppercase tracking-wider text-white/40 mb-1">Directionnel</div>
              <div className="text-sm">{profile.profile.directional}</div>
            </div>
            <div className="bg-hover rounded-md p-3 border border-warn/20">
              <div className="text-[11px] uppercase tracking-wider text-white/40 mb-1">Conseil de style</div>
              <div className="text-sm">{profile.profile.style_advice}</div>
            </div>
            <div className="bg-hover rounded-md p-3 border border-white/5">
              <div className="text-[11px] uppercase tracking-wider text-white/40 mb-1">Signaux ≥ 80% confiance</div>
              <div className="text-sm">
                Winrate haute confiance : <span className="font-mono text-accent-blue">{profile.high_confidence_winrate}%</span>
              </div>
            </div>
          </div>

          {/* Top symbols & timeframes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="panel overflow-hidden">
              <div className="panel-title text-[14px] p-4 pb-2">Symboles les plus rentables</div>
              {profile.top_symbols.length === 0 && <div className="text-white/40 text-xs p-4">Pas assez de données par symbole.</div>}
              <table className="dt">
                <thead><tr><th>Symbole</th><th>Trades</th><th>Winrate</th></tr></thead>
                <tbody>
                  {profile.top_symbols.map((s) => (
                    <tr key={s.symbol}>
                      <td className="font-medium">{s.symbol}</td>
                      <td>{s.n_trades}</td>
                      <td className={s.winrate >= 50 ? "text-up" : "text-down"}>{s.winrate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="panel overflow-hidden">
              <div className="panel-title text-[14px] p-4 pb-2">Timeframes les plus efficaces</div>
              {profile.best_timeframes.length === 0 && <div className="text-white/40 text-xs p-4">Pas assez de données par timeframe.</div>}
              <table className="dt">
                <thead><tr><th>Timeframe</th><th>Trades</th><th>Winrate</th></tr></thead>
                <tbody>
                  {profile.best_timeframes.map((s) => (
                    <tr key={s.timeframe}>
                      <td className="font-medium font-mono">{s.timeframe}</td>
                      <td>{s.n_trades}</td>
                      <td className={s.winrate >= 50 ? "text-up" : "text-down"}>{s.winrate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Immutable audit log */}
      <div className="panel overflow-hidden" data-testid="audit-log">
        <div className="panel-title text-[14px] p-4 pb-2 flex items-center gap-2 justify-between">
          <span className="flex items-center gap-2"><Archive size={14} /> Journal d'audit (hash-chain SHA-256)</span>
          {auditVerify?.final_hash && (
            <span className="font-mono text-[10px] text-white/40" title={auditVerify.final_hash}>
              🔒 {auditVerify.final_hash.slice(0, 12)}…
            </span>
          )}
        </div>
        <table className="dt">
          <thead>
            <tr><th>Horodatage</th><th>Événement</th><th>Détails</th></tr>
          </thead>
          <tbody>
            {events.length === 0 && (
              <tr><td colSpan={3} className="text-center text-white/40 py-10">Aucun événement encore enregistré. Générez des signaux pour peupler le journal.</td></tr>
            )}
            {events.map((e, i) => (
              <tr key={i}>
                <td className="font-mono text-[11px] text-white/60">{fmtDate(e.timestamp)}</td>
                <td>
                  <span className="chip text-[10px]">{e.event_type}</span>
                </td>
                <td className="text-[11px] font-mono text-white/70">
                  {(() => {
                    const p = e.payload || {};
                    if (e.event_type === "signal_generated") {
                      return `${p.symbol} ${p.timeframe} · ${p.direction} @ ${p.entry} · RR ${p.rr} · conf ${p.confidence}%`;
                    }
                    return JSON.stringify(p).slice(0, 80);
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, color = "text-white", icon: Icon }) {
  return (
    <div className="panel p-3">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
        {Icon && <Icon size={12} className="text-white/30" />}
      </div>
      <div className={`font-mono text-2xl ${color} mt-1`}>{value}</div>
    </div>
  );
}
